import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Clock3, Send, Terminal, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { serverApi } from '../services/api';
import type { MinecraftConsoleResult } from '../types/server';
import { getApiErrorDetails } from '../utils/apiError';

interface MinecraftConsoleModalProps {
  serverId: string;
  serverName: string;
  actualState?: string;
  agentConnected: boolean;
  supported: boolean;
  onClose: () => void;
}

interface ConsoleTranscriptEntry {
  commandId: string;
  command: string;
  status: 'sending' | 'succeeded' | 'failed';
  sentAt: string;
  result?: MinecraftConsoleResult;
  errorCode?: string;
  errorMessage?: string;
}

const ERROR_TRANSLATION_KEYS: Record<string, string> = {
  console_timeout: 'minecraftConsole.errors.timeout',
  console_result_unknown: 'minecraftConsole.errors.resultUnknown',
  console_agent_offline: 'minecraftConsole.errors.agentOffline',
  console_server_stopped: 'minecraftConsole.errors.serverStopped',
  console_server_busy: 'minecraftConsole.errors.serverBusy',
  console_unsupported: 'minecraftConsole.errors.unsupported',
  console_runtime_mismatch: 'minecraftConsole.errors.unsupported',
  console_rcon_unavailable: 'minecraftConsole.errors.rconUnavailable',
};

export function MinecraftConsoleModal({
  serverId,
  serverName,
  actualState,
  agentConnected,
  supported,
  onClose,
}: MinecraftConsoleModalProps) {
  const { t } = useTranslation();
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [transcript, setTranscript] = useState<ConsoleTranscriptEntry[]>([]);
  const [sending, setSending] = useState(false);
  const historyDraftRef = useRef('');
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const unavailableMessage = useMemo(() => {
    if (!supported) return t('minecraftConsole.errors.unsupported');
    if (actualState !== 'running') return t('minecraftConsole.errors.serverStopped');
    if (!agentConnected) return t('minecraftConsole.errors.agentOffline');
    return '';
  }, [actualState, agentConnected, supported, t]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const updateTranscript = (
    commandId: string,
    update: Partial<ConsoleTranscriptEntry>,
  ) => {
    setTranscript(current => current.map(entry => (
      entry.commandId === commandId ? { ...entry, ...update } : entry
    )));
  };

  const submitCommand = async (event?: FormEvent) => {
    event?.preventDefault();
    const normalized = command.trim();
    if (!normalized || sending || unavailableMessage) return;

    const commandId = crypto.randomUUID();
    const entry: ConsoleTranscriptEntry = {
      commandId,
      command: normalized,
      status: 'sending',
      sentAt: new Date().toISOString(),
    };
    setTranscript(current => [...current, entry]);
    setHistory(current => [...current, normalized]);
    setHistoryIndex(null);
    historyDraftRef.current = '';
    setCommand('');
    setSending(true);

    try {
      const response = await serverApi.executeMinecraftConsoleCommand(serverId, {
        commandId,
        command: normalized,
      });
      if (!response.success) {
        const translationKey = ERROR_TRANSLATION_KEYS[response.error.code];
        updateTranscript(commandId, {
          status: 'failed',
          errorCode: response.error.code,
          errorMessage: translationKey ? t(translationKey) : response.error.message,
        });
        return;
      }
      updateTranscript(commandId, { status: 'succeeded', result: response.result });
    } catch (error) {
      const details = getApiErrorDetails(error, t('minecraftConsole.errors.unavailable'));
      const translationKey = details.code ? ERROR_TRANSLATION_KEYS[details.code] : undefined;
      updateTranscript(commandId, {
        status: 'failed',
        errorCode: details.code,
        errorMessage: translationKey ? t(translationKey) : details.message,
      });
    } finally {
      setSending(false);
    }
  };

  const handleCommandKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submitCommand();
      return;
    }
    if (event.key === 'ArrowUp') {
      if (history.length === 0) return;
      event.preventDefault();
      const nextIndex = historyIndex === null
        ? history.length - 1
        : Math.max(0, historyIndex - 1);
      if (historyIndex === null) historyDraftRef.current = command;
      setHistoryIndex(nextIndex);
      setCommand(history[nextIndex]);
      return;
    }
    if (event.key === 'ArrowDown' && historyIndex !== null) {
      event.preventDefault();
      if (historyIndex >= history.length - 1) {
        setHistoryIndex(null);
        setCommand(historyDraftRef.current);
        return;
      }
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setCommand(history[nextIndex]);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-5xl h-[80vh] flex flex-col"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex justify-between items-center p-5 border-b border-dark-600">
          <h2 className="text-xl font-bold text-gray-100 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-neon-400" />
            {t('minecraftConsole.title', { name: serverName })}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTranscript([])}
              className="btn-secondary text-sm px-3 py-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('minecraftConsole.clear')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost p-2"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-4 flex flex-col gap-3">
          {unavailableMessage && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-2 text-sm text-yellow-300">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {unavailableMessage}
            </div>
          )}

          <div className="flex-1 overflow-y-auto bg-dark-950 rounded-lg p-4 font-mono text-sm scrollbar-thin">
            {transcript.length === 0 ? (
              <div className="text-gray-600 text-center py-8">
                {t('minecraftConsole.empty')}
              </div>
            ) : transcript.map(entry => (
              <div key={entry.commandId} className="mb-4 last:mb-0" data-command-status={entry.status}>
                <div className="text-neon-400 whitespace-pre-wrap break-words">
                  <span className="select-none text-gray-600">&gt; </span>{entry.command}
                </div>
                {entry.status === 'sending' && (
                  <div className="text-blue-300 flex items-center gap-2 mt-1">
                    <div className="spinner w-3 h-3" />
                    {t('minecraftConsole.sending')}
                  </div>
                )}
                {entry.status === 'failed' && (
                  <div className="text-red-400 mt-1 whitespace-pre-wrap break-words">
                    {entry.errorMessage}
                  </div>
                )}
                {entry.status === 'succeeded' && entry.result && (
                  <div className="mt-1">
                    <div className="text-gray-300 whitespace-pre-wrap break-words">
                      {entry.result.output || t('minecraftConsole.noOutput')}
                    </div>
                    <div className="text-gray-600 text-xs mt-1 flex items-center gap-1.5">
                      <Clock3 className="w-3 h-3" />
                      {t('minecraftConsole.duration', { duration: entry.result.durationMs })}
                      {entry.result.truncated ? ` · ${t('minecraftConsole.truncated')}` : ''}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>

          <form onSubmit={submitCommand} className="flex gap-2">
            <input
              value={command}
              onChange={event => {
                setCommand(event.target.value);
                if (historyIndex !== null) setHistoryIndex(null);
              }}
              onKeyDown={handleCommandKeyDown}
              maxLength={1_024}
              disabled={sending || Boolean(unavailableMessage)}
              className="input flex-1 font-mono"
              placeholder={t('minecraftConsole.placeholder')}
              aria-label={t('minecraftConsole.commandLabel')}
              autoFocus
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={sending || Boolean(unavailableMessage) || command.trim().length === 0}
              className="btn-primary"
            >
              {sending ? <div className="spinner w-4 h-4" /> : <Send className="w-4 h-4" />}
              {t('minecraftConsole.send')}
            </button>
          </form>
          <div className="text-xs text-gray-600">
            {t('minecraftConsole.historyHint')}
          </div>
        </div>
      </div>
    </div>
  );
}
